/**
 * markdown-view.tsx — Hiển thị Markdown đẹp: bảng (GFM), sơ đồ mermaid, mục
 * lục theo H2/H3, ghi chú ⚠️ thành hộp cảnh báo, link ngoài mở tab mới.
 * Dùng cho Đào tạo nội bộ; giữ độc lập để nơi khác tái dùng.
 */
import { createElement, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { MessageSquarePlus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { KhoiNeo } from '@/lib/neo-nhan-xet'

/**
 * Chế độ nhận xét theo khối: mỗi khối (tiêu đề, đoạn, bảng, mục danh sách,
 * code, sơ đồ) mang `data-dong` = dòng bắt đầu trong markdown và hiện nút
 * "Nhận xét" khi rê chuột. Trang Đào tạo dùng để người giám sát góp ý đúng chỗ.
 */
export interface TuyChonKhoi {
  /** Bấm nút nhận xét trên một khối. */
  onNhanXet?: (khoi: KhoiNeo) => void
  /** Số nhận xét đang mở theo dòng khối → badge + viền. */
  soNhanXet?: Map<number, number>
  /** Khối đang được chọn/nổi bật (dòng). */
  khoiNoiBat?: number | null
  /** Danh sách khối sau khi render — để neo lại nhận xét khi tài liệu đổi. */
  onKhoiSanSang?: (khoi: KhoiNeo[]) => void
}

/** Slug tiêu đề (dùng cho mục lục + anchor). */
export function slugHeading(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export interface TocItem { level: 2 | 3; text: string; id: string }

/** Rút mục lục từ markdown thô (bỏ code fence). */
export function tocFromMarkdown(md: string): TocItem[] {
  const out: TocItem[] = []
  let trongFence = false
  const dem = new Map<string, number>()
  for (const line of md.split('\n')) {
    if (/^```/.test(line.trim())) { trongFence = !trongFence; continue }
    if (trongFence) continue
    const m = line.match(/^(##|###)\s+(.+?)\s*#*\s*$/)
    if (!m) continue
    const text = m[2].replace(/[*_`]/g, '').trim()
    let id = slugHeading(text) || 'muc'
    const n = (dem.get(id) ?? 0) + 1
    dem.set(id, n)
    if (n > 1) id = `${id}-${n}`
    out.push({ level: m[1].length as 2 | 3, text, id })
  }
  return out
}

function textOf(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (typeof node === 'object' && 'props' in node) return textOf((node as { props: { children?: ReactNode } }).props.children)
  return ''
}

/**
 * Nhãn nút mermaid chứa ( ) hoặc ' " mà không bọc trong dấu nháy thì mermaid
 * báo lỗi cú pháp. SOP viết tay hay gặp: `B[Gán CSKH (round-robin)]`. Tự bọc
 * `["…"]` cho nhãn trong [] và {} khi cần; để nguyên nếu đã có nháy.
 */
export function chuanHoaMermaid(code: string): string {
  return code.replace(/([A-Za-z0-9_]+)([\[{])([^\]}"\n]*?)([\]}])/g, (all, id, mo, nhan, dong) => {
    if (!/[()'"<>]/.test(nhan)) return all
    return `${id}${mo}"${nhan.replace(/"/g, '#quot;')}"${dong}`
  })
}

/** Sơ đồ mermaid — nạp thư viện khi cần (nặng), lỗi cú pháp thì hiện mã nguồn. */
function Mermaid({ code: goc }: { code: string }) {
  const code = useMemo(() => chuanHoaMermaid(goc), [goc])
  const id = useId().replace(/[^a-zA-Z0-9]/g, '')
  const ref = useRef<HTMLDivElement>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const m = (await import('mermaid')).default
        m.initialize({ startOnLoad: false, theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default', securityLevel: 'strict', fontFamily: 'inherit' })
        const { svg } = await m.render(`mmd-${id}`, code)
        if (alive && ref.current) { ref.current.innerHTML = svg; setErr(null) }
      } catch (e) {
        if (alive) setErr((e as Error).message)
      }
    })()
    return () => { alive = false }
  }, [code, id])
  if (err) {
    return (
      <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
        <p className="mb-1 font-semibold text-warning">Sơ đồ không vẽ được — hiện mã nguồn</p>
        <pre className="whitespace-pre-wrap">{code}</pre>
      </div>
    )
  }
  return <div ref={ref} className="md-mermaid my-4 overflow-x-auto rounded-md border bg-background p-3 [&_svg]:mx-auto [&_svg]:max-w-full" />
}

type NodeViTri = { position?: { start: { line: number } } }
const TRICH_DOAN = 160

export function MarkdownView({ content, className, khoi }: { content: string; className?: string; khoi?: TuyChonKhoi }) {
  // Gom khối trong lúc render (theo dòng), báo lên sau khi render xong.
  const khoiRef = useRef<Map<number, string>>(new Map())
  khoiRef.current = new Map()
  const onKhoiSanSang = khoi?.onKhoiSanSang
  useEffect(() => {
    if (!onKhoiSanSang) return
    onKhoiSanSang([...khoiRef.current.entries()].sort((a, b) => a[0] - b[0]).map(([line, quote]) => ({ line, quote })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, onKhoiSanSang])

  /** Bọc một phần tử khối: gắn dòng, nút nhận xét, badge. Không có `khoi` thì trả phần tử thường. */
  const Khoi = (tag: string, node: NodeViTri | undefined, children: ReactNode, props: Record<string, unknown> = {}, trichDoan?: string) => {
    const line = node?.position?.start.line
    if (!khoi || !line) return createElement(tag, props, children)
    const quote = (trichDoan ?? textOf(children)).replace(/\s+/g, ' ').trim().slice(0, TRICH_DOAN)
    if (!khoiRef.current.has(line)) khoiRef.current.set(line, quote)
    const so = khoi.soNhanXet?.get(line) ?? 0
    const noiBat = khoi.khoiNoiBat === line
    return createElement(
      tag,
      {
        ...props,
        'data-dong': line,
        className: cn(props.className as string | undefined, 'md-khoi', so > 0 && 'md-khoi-co-nhan-xet', noiBat && 'md-khoi-noi-bat'),
      },
      children,
      khoi.onNhanXet && (
        <button
          type="button"
          className="nut-nhan-xet"
          title={so > 0 ? `${so} nhận xét — thêm nhận xét` : 'Nhận xét khối này'}
          aria-label="Nhận xét khối này"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); khoi.onNhanXet?.({ line, quote }) }}
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
          {so > 0 && <span className="nut-nhan-xet-so">{so}</span>}
        </button>
      ),
    )
  }

  const headingIds = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const t of tocFromMarkdown(content)) {
      const arr = map.get(t.text) ?? []
      arr.push(t.id)
      map.set(t.text, arr)
    }
    return map
  }, [content])
  // Cùng một tiêu đề lặp lại → phát id theo thứ tự gặp (khớp tocFromMarkdown).
  const daPhat = useRef(new Map<string, number>())
  daPhat.current = new Map()
  const idCho = (text: string) => {
    const ids = headingIds.get(text)
    if (!ids) return slugHeading(text)
    const i = daPhat.current.get(text) ?? 0
    daPhat.current.set(text, i + 1)
    return ids[Math.min(i, ids.length - 1)]
  }

  return (
    <div className={cn('md-prose', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children, node }) => Khoi('h1', node as NodeViTri, children),
          h2: ({ children, node }) => Khoi('h2', node as NodeViTri, children, { id: idCho(textOf(children)) }),
          h3: ({ children, node }) => Khoi('h3', node as NodeViTri, children, { id: idCho(textOf(children)) }),
          h4: ({ children, node }) => Khoi('h4', node as NodeViTri, children),
          p: ({ children, node }) => Khoi('p', node as NodeViTri, children),
          li: ({ children, node, ...rest }) => Khoi('li', node as NodeViTri, children, rest as Record<string, unknown>),
          a: ({ href, children }) => {
            const ngoai = !!href && /^https?:\/\//.test(href)
            return <a href={href} target={ngoai ? '_blank' : undefined} rel={ngoai ? 'noreferrer' : undefined}>{children}</a>
          },
          table: ({ children, node }) => Khoi('div', node as NodeViTri, <table>{children}</table>, { className: 'md-table-wrap' }),
          code: ({ className: cls, children, node, ...rest }) => {
            const lang = /language-(\w+)/.exec(cls ?? '')?.[1]
            const code = String(children).replace(/\n$/, '')
            // Sơ đồ/code: trích đoạn = mã nguồn (SVG mermaid không có chữ đọc được).
            if (lang === 'mermaid') return Khoi('div', node as NodeViTri, <Mermaid code={code} />, { className: 'md-khoi-so-do' }, 'Sơ đồ: ' + code)
            // Khối code (có xuống dòng hoặc có language) vs code inline
            if (lang || code.includes('\n')) return Khoi('pre', node as NodeViTri, <code className={cls} {...rest}>{code}</code>, {}, code)
            return <code className="md-inline" {...rest}>{children}</code>
          },
          pre: ({ children }) => <>{children}</>,
          blockquote: ({ children, node }) => {
            const t = textOf(children).trim()
            const canhBao = /^(⚠️|❗|❌|CẢNH BÁO|LƯU Ý)/i.test(t)
            const meo = /^(💡|✅|MẸO|GỢI Ý)/i.test(t)
            return Khoi('blockquote', node as NodeViTri, children, { className: cn(canhBao && 'md-warn', meo && 'md-tip') })
          },
          input: ({ checked, ...rest }) => <input type="checkbox" checked={!!checked} readOnly className="mr-1.5 align-middle" {...rest} />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
